import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Divider,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { alpha } from '@mui/material/styles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Page from '@/components/Page';
import Iconify from '@/components/Iconify';
import EmptyContent from '@/components/EmptyContent';
import useSettings from '@/hooks/useSettings';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { getNotificationRequest, getNotificationsReadAllRequest } from '../api';
import { NotificationItem } from '../components';
import {
  NotificationCategoryKey,
  applySortFilter,
  getNotificationCategoryOptions,
  groupNotificationsByDate,
  hasRenderableNotification,
} from '../utils';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'read', label: 'Read' },
] as const;

type FilterStatus = (typeof STATUS_OPTIONS)[number]['value'];
type CategoryFilter = NotificationCategoryKey | 'all';

const NOTIFICATIONS_PAGE_LIMIT = 25;
const EMPTY_PAGINATION = {
  page: 1,
  limit: NOTIFICATIONS_PAGE_LIMIT,
  total: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

export default function NotificationsList() {
  const { themeStretch } = useSettings();
  const queryClient = useQueryClient();
  const { errorMsg, successMsg } = useSnackbarMsg();
  const [currentPage, setCurrentPage] = useState(1);
  const [filterName, setFilterName] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [filterCategory, setFilterCategory] = useState<CategoryFilter>('all');

  const { data, isFetching, isLoading } = useQuery({
    queryKey: ['notifications', 'list', currentPage, NOTIFICATIONS_PAGE_LIMIT],
    queryFn: () =>
      getNotificationRequest({
        page: currentPage,
        limit: NOTIFICATIONS_PAGE_LIMIT,
      }),
    keepPreviousData: true,
    refetchInterval: 30000,
  });

  const tableData = useMemo(
    () => (data?.notifications ?? []).filter(hasRenderableNotification),
    [data?.notifications]
  );
  const categoryOptions = useMemo(
    () => getNotificationCategoryOptions(tableData),
    [tableData]
  );
  const pagination = data?.pagination ?? EMPTY_PAGINATION;
  const hasUnreadNotifications = (data?.totalUnseen ?? 0) > 0;
  const unreadCount = data?.totalUnseen ?? 0;
  const initialLoading = isLoading && !data;
  const totalNotifications = pagination.total;

  const readAllMutation = useMutation({
    mutationFn: getNotificationsReadAllRequest,
    onSuccess: () => {
      setCurrentPage(1);
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'popover'] });
      successMsg('All notifications marked as read');
    },
    onError: (error) => {
      errorMsg(error, 'There was an error while marking notifications as read');
    },
  });

  const handleReadAll = useCallback(() => {
    if (!hasUnreadNotifications) return;

    readAllMutation.mutate();
  }, [hasUnreadNotifications, readAllMutation]);

  const handleFilterName = (value: string) => {
    setFilterName(value);
    setCurrentPage(1);
  };

  const handleFilterStatus = (
    _event: React.MouseEvent<HTMLElement>,
    value: FilterStatus | null
  ) => {
    if (!value) return;

    setFilterStatus(value);
    setCurrentPage(1);
  };

  const handleFilterCategory = (value: CategoryFilter) => {
    setFilterCategory(value);
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    if (!pagination.hasPreviousPage) return;

    setCurrentPage((page) => Math.max(1, page - 1));
  };

  const handleNextPage = () => {
    if (!pagination.hasNextPage) return;

    setCurrentPage((page) => page + 1);
  };

  const dataFiltered = useMemo(
    () =>
      applySortFilter({
        tableData,
        filterName,
        filterTable: 'all',
        filterStatus,
        filterCategory,
        filterFrom: null,
        filterTo: null,
      }),
    [filterCategory, filterName, filterStatus, tableData]
  );

  const notificationGroups = useMemo(
    () => groupNotificationsByDate(dataFiltered),
    [dataFiltered]
  );
  const hasFilters =
    Boolean(filterName) || filterStatus !== 'all' || filterCategory !== 'all';
  const showPagination =
    pagination.totalPages > 1 ||
    pagination.hasNextPage ||
    pagination.hasPreviousPage;

  return (
    <Page title="Notifications">
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <Card sx={{ p: { xs: 2, md: 4 }, mt: { xs: 2, md: 3 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h3" sx={{ mb: 0.75 }}>
                Notifications
              </Typography>
              {initialLoading ? (
                <Skeleton width={180} height={26} />
              ) : (
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'text.secondary' }}
                >
                  {totalNotifications} notification
                  {totalNotifications === 1 ? '' : 's'}
                  {unreadCount > 0 && (
                    <Box
                      component="span"
                      sx={{ color: 'primary.main', fontWeight: 700 }}
                    >
                      {' '}
                      · {unreadCount} unread
                    </Box>
                  )}
                </Typography>
              )}
            </Box>

            <LoadingButton
              color="primary"
              variant="outlined"
              loading={readAllMutation.isLoading}
              disabled={!hasUnreadNotifications}
              startIcon={<Iconify icon="eva:done-all-fill" />}
              onClick={handleReadAll}
              sx={{
                borderRadius: 1,
                px: 2.25,
                alignSelf: { xs: 'stretch', sm: 'flex-start', md: 'center' },
              }}
            >
              Mark all as read
            </LoadingButton>
          </Stack>

          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', lg: 'center' }}
            justifyContent="space-between"
            sx={{ mt: 4 }}
          >
            <ToggleButtonGroup
              exclusive
              value={filterStatus}
              onChange={handleFilterStatus}
              aria-label="Notification status"
              sx={{
                width: { xs: '100%', sm: 'auto' },
                p: 0.5,
                bgcolor: 'background.neutral',
                borderRadius: 1,
                '& .MuiToggleButton-root': {
                  border: 0,
                  px: 2.5,
                  py: 0.85,
                  minWidth: 88,
                  borderRadius: 0.75,
                  typography: 'subtitle2',
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    boxShadow: (theme) => theme.customShadows.z8,
                  },
                  '&.Mui-selected:hover': {
                    bgcolor: 'background.paper',
                  },
                },
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{
                flex: 1,
                justifyContent: { xs: 'flex-start', lg: 'center' },
              }}
            >
              <Chip
                clickable
                label="All types"
                color={filterCategory === 'all' ? 'primary' : 'default'}
                variant={filterCategory === 'all' ? 'filled' : 'outlined'}
                onClick={() => handleFilterCategory('all')}
                sx={{ borderRadius: 1, fontWeight: 700 }}
              />
              {categoryOptions.map((category) => (
                <Chip
                  clickable
                  key={category.key}
                  icon={
                    <Iconify
                      icon={category.icon}
                      width={16}
                      sx={{
                        color:
                          filterCategory === category.key
                            ? 'common.white'
                            : category.color,
                      }}
                    />
                  }
                  label={category.label}
                  variant={
                    filterCategory === category.key ? 'filled' : 'outlined'
                  }
                  onClick={() => handleFilterCategory(category.key)}
                  sx={{
                    borderRadius: 1,
                    fontWeight: 700,
                    color:
                      filterCategory === category.key
                        ? 'common.white'
                        : 'text.primary',
                    borderColor: alpha(category.color, 0.36),
                    bgcolor:
                      filterCategory === category.key
                        ? category.color
                        : alpha(category.color, 0.04),
                    '&:hover': {
                      bgcolor:
                        filterCategory === category.key
                          ? category.color
                          : alpha(category.color, 0.1),
                    },
                  }}
                />
              ))}
            </Stack>

            <TextField
              value={filterName}
              onChange={(event) => handleFilterName(event.target.value)}
              placeholder="Search notifications..."
              sx={{ width: { xs: '100%', lg: 320 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify
                      icon="eva:search-fill"
                      width={20}
                      sx={{ color: 'text.disabled' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <Divider sx={{ mt: 3, mb: 1.5 }} />

          {initialLoading ? (
            <Stack spacing={1.5}>
              {[1, 2, 3, 4].map((item) => (
                <Stack
                  key={item}
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ px: 1, py: 1.5 }}
                >
                  <Skeleton variant="rounded" width={44} height={44} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="70%" height={24} />
                    <Skeleton width="28%" height={18} />
                  </Box>
                </Stack>
              ))}
            </Stack>
          ) : dataFiltered.length ? (
            <Stack spacing={2}>
              {notificationGroups.map((group) => (
                <Box key={group.key}>
                  <Typography
                    variant="overline"
                    sx={{
                      display: 'block',
                      color: 'text.disabled',
                      letterSpacing: 0,
                      mb: 0.5,
                    }}
                  >
                    {group.label}
                  </Typography>
                  {group.notifications.map((notificationObject) => (
                    <NotificationItem
                      key={notificationObject.id}
                      notificationObject={notificationObject}
                    />
                  ))}
                </Box>
              ))}
            </Stack>
          ) : (
            <EmptyContent
              title={
                hasFilters ? 'No notifications found' : 'No notifications yet'
              }
              description={
                hasFilters
                  ? 'Try a different status, type, or search term.'
                  : 'New activity will appear here when it arrives.'
              }
              sx={{ py: 8 }}
            />
          )}

          {showPagination && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              sx={{ mt: 3 }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Page {pagination.page} of {Math.max(pagination.totalPages, 1)}
              </Typography>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                  disabled={!pagination.hasPreviousPage || isFetching}
                  onClick={handlePreviousPage}
                  sx={{ borderRadius: 1 }}
                >
                  Previous
                </Button>
                <Button
                  variant="outlined"
                  endIcon={<Iconify icon="eva:arrow-ios-forward-fill" />}
                  disabled={!pagination.hasNextPage || isFetching}
                  onClick={handleNextPage}
                  sx={{ borderRadius: 1 }}
                >
                  Next
                </Button>
              </Stack>
            </Stack>
          )}
        </Card>
      </Container>
    </Page>
  );
}
